import { Flex, Tour, Typography, usePaypingContext } from "@payping/web-components";
import type { TourProps } from "@payping/web-components/components/tour/types";
import { getTourTarget, useAppTranslation, waitForTourElement } from "@/utils";
import { overviewTourIds } from "@/assets/tourIds";
import { useState } from "react";
import { useAsync } from "react-use";
import { userApis } from "@payping/api";
import { useImmerAtom } from "jotai-immer";
import { dashboardPreferencesAtom } from "@/core/state";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";

const iSSeenAtom = selectAtom(dashboardPreferencesAtom, (val) => val.tours.overview.isSeen);

const OverviewTour = () => {
  const { t } = useAppTranslation();
  const [current, setCurrent] = useState<number>(0);
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;

  const [, setDashboardPreferences] = useImmerAtom(dashboardPreferencesAtom);
  const [iSSeen] = useAtom(iSSeenAtom);

  const triggerHoverOrClick = (targetId: string) => {
    const element = getTourTarget(targetId);

    if (!element) {
      return null;
    }

    // Fire the same events a user would so the popover opens even with interactions disabled.
    (["mouseenter", "mouseover", "click"] as const).forEach((eventName) => {
      element.dispatchEvent(new MouseEvent(eventName, { bubbles: true, cancelable: true }));
    });

    return element;
  };

  const getTargetElement = (targetId: string) =>
    getTourTarget(targetId) ?? document.body ?? document.documentElement ?? document.createElement("div");

  function goNext() {
    setCurrent((prev) => prev + 1);
  }
  function goPrev() {
    setCurrent((prev) => prev - 1);
  }

  const { value: isLaunchDate } = useAsync(async () => {
    const value = await userApis.userDetailWithCancellationTokenGet();

    if (value?.registerDateTime) {
      const registerDate = new Date(value?.registerDateTime);
      const launchDate = new Date("2024-11-09T00:00:00Z");
      return registerDate > launchDate;
    }
    return false;
  }, []);

  const steps: TourProps["steps"] = [
    ...(isLaunchDate
      ? [
          {
            title: t("tour.overview.welcome.title"),
            description: (
              <Flex vertical>
                {(t("tour.overview.welcome.description", { returnObjects: true }) as string[])?.map(
                  (text: string, index: number) => (
                    <Typography.Text key={index}>{text}</Typography.Text>
                  ),
                )}
              </Flex>
            ),
            onPrev: () => goPrev(),
            onNext: () => goNext(),
          },
        ]
      : []),
    {
      title: t("tour.overview.overview.title"),
      description: (
        <Flex vertical>
          {(t("tour.overview.overview.description", { returnObjects: true }) as string[])?.map(
            (text: string, index: number) => (
              <Typography.Text key={index}>{text}</Typography.Text>
            ),
          )}
        </Flex>
      ),
      target: () => getTargetElement(overviewTourIds.overviewTab),
      onPrev: () => goPrev(),
      onNext: () => goNext(),
    },
    {
      title: t("tour.overview.report.title"),
      description: (
        <Flex vertical>
          {(t("tour.overview.report.description", { returnObjects: true }) as string[])?.map(
            (text: string, index: number) => (
              <Typography.Text key={index}>{text}</Typography.Text>
            ),
          )}
        </Flex>
      ),
      target: () => getTargetElement(overviewTourIds.saleTab),
      onPrev: () => goPrev(),
      onNext: () => goNext(),
    },
    {
      title: t("tour.overview.solutions.title"),
      description: (
        <Flex vertical>
          {(t("tour.overview.solutions.description", { returnObjects: true }) as string[])?.map(
            (text: string, index: number) => (
              <Typography.Text key={index}>{text}</Typography.Text>
            ),
          )}
        </Flex>
      ),
      target: () => getTargetElement(overviewTourIds.shopTab),
      onPrev: () => goPrev(),
      onNext: () => goNext(),
    },
    {
      title: t("tour.overview.connections.title"),
      description: (
        <Flex vertical>
          {(t("tour.overview.connections.description", { returnObjects: true }) as string[])?.map(
            (text: string, index: number) => (
              <Typography.Text key={index}>{text}</Typography.Text>
            ),
          )}
        </Flex>
      ),
      target: () => getTargetElement(overviewTourIds.connectionTab),
      onPrev: () => goPrev(),
      onNext: async () => {
        if (lg) {
          const anchor = triggerHoverOrClick(overviewTourIds.businessSettingAnchor);
          if (anchor) {
            await waitForTourElement(overviewTourIds.businessSettingDropdown);
          }
        }
        goNext();
      },
    },
    {
      title: t("tour.overview.businessSettings.title"),
      description: (
        <Flex vertical>
          {(t("tour.overview.businessSettings.description", { returnObjects: true }) as string[])?.map(
            (text: string, index: number) => (
              <Typography.Text key={index}>{text}</Typography.Text>
            ),
          )}
        </Flex>
      ),
      target: () =>
        getTargetElement(lg ? overviewTourIds.businessSettingDropdown : overviewTourIds.businessSettingAnchor),
      onPrev: () => goPrev(),
      onNext: async () => {
        if (lg) {
          const anchor = triggerHoverOrClick(overviewTourIds.profileAnchor);
          if (anchor) {
            await waitForTourElement(overviewTourIds.profileDropdown);
          }
        }
        goNext();
      },
    },
    ...[
      {
        title: t("tour.overview.profile.title"),
        description: (
          <Flex vertical>
            {(t("tour.overview.profile.description", { returnObjects: true }) as string[])?.map(
              (text: string, index: number) => (
                <Typography.Text key={index}>{text}</Typography.Text>
              ),
            )}
          </Flex>
        ),
        target: () => getTargetElement(lg ? overviewTourIds.profileDropdown : overviewTourIds.profileAnchor),
        onPrev: async () => {
          if (lg) {
            const anchor = triggerHoverOrClick(overviewTourIds.businessSettingAnchor);
            if (anchor) {
              await waitForTourElement(overviewTourIds.businessSettingDropdown);
            }
          }
          goPrev();
        },
      },
    ],
  ];

  const handleFinish = () => {
    setDashboardPreferences((draft) => {
      // eslint-disable-next-line no-param-reassign
      draft.tours.overview.isSeen = true;
    });
  };

  return (
    <Tour
      open={!iSSeen}
      current={current}
      onClose={handleFinish}
      onFinish={handleFinish}
      disabledInteraction={true}
      steps={steps}
    />
  );
};

export default OverviewTour;
